import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, 'toString', {
            value: () => '',
            enumerable: false,
        });

    const getDimensions = (size, sizes) => {
        if (size && typeof size.width === 'number' && typeof size.height === 'number') {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return '';
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule = fillColor && fillColorRule ? `${fillColorRule}{ fill: ${fillColor}; }` : '';

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes,
    };
};

const width = '1024';
const height = '1024';
const viewBox = '0 0 1024 1024';

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(width, height, css);

const sizes = sanitizeSizes({
    small: { width: 16, height: 16 },
    medium: { width: 18, height: 18 },
    large: { width: 24, height: 24 },
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            d="M512 132.762A379.392 379.392 0 00132.762 512 379.392 379.392 0 00512 891.238 379.392 379.392 0 00891.238 512 379.392 379.392 0 00512 132.762zm37.888 644.71h-75.776v-75.827h75.776zm78.54-293.888l-34.15 34.816c-27.29 27.75-44.339 50.483-44.339 107.366h-75.878v-18.944c0-41.728 17.1-79.667 44.39-107.315l47.002-47.82c14.08-13.62 22.374-32.615 22.374-53.453 0-41.728-34.099-75.879-75.827-75.879s-75.827 34.15-75.827 75.879h-75.879a151.654 151.654 0 11303.412 0 120.62 120.62 0 01-35.277 85.299z"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: '&&& path, &&& use, &&& g',
    sizes,
    size: null,
};

const propTypes = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired,
        }),
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number,
    }),
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    propTypes,
    displayName: 'Help',
});
